import { useState } from "react";
import { useForm } from "@inertiajs/react";
import { Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import MoneyField from "@/components/forms/MoneyField";
import CampaignWindowFields from "@/components/schools/CampaignWindowFields";
import { WINDOW_DEFAULTS } from "@/lib/campaignWindows";
import { cn } from "@/lib/utils";
import {
  suggestDates, suggestCampaignName, dateAdvice, keyDatesFor, suggestMinuteGoal,
  MINUTES_PER_DAY,
} from "@/lib/campaignPlanning";
import { StepNotice } from "@/components/schools/setup/SetupPrimitives";

/**
 * The setup guide's campaign step: creates the campaign, then stays as
 * the place to change it (Save lights up when something changes), with
 * the suggestions a founder would make across the table: dates with a
 * proper runway, a name to match the season, a reading goal worked out
 * from the number of students, and advice on anything unusual.
 *
 * mode: "create" (no campaign yet), "edit", or "confirm" (the draft made
 * at signup, whose dates and name are placeholders: suggest them as if
 * creating, and save even unchanged, since saving confirms the dates).
 */
export default function CampaignPlanForm({ mode, setup }) {
  const { campaign_details: campaign, application, key_dates: { today }, earliest_start_on, counts, paths } = setup;
  const suggesting = mode !== "edit";

  const initial = !suggesting
    ? { start: campaign.start_on, end: campaign.end_on, fromApplication: false }
    : suggestDates({
        today,
        earliestStart: earliest_start_on,
        applicationStart: application?.campaign_start_on,
        applicationEnd: application?.campaign_end_on,
      });

  const form = useForm({
    name: suggesting ? suggestCampaignName(initial.start) : campaign.name,
    start_at: initial.start,
    end_at: initial.end,
    description: campaign?.description ?? "",
    money_goal_dollars: campaign?.money_goal_cents ? String(campaign.money_goal_cents / 100) : "",
    minute_goal: campaign?.minute_goal ? String(campaign.minute_goal) : "",
    donation_lead_days: campaign ? campaign.donation_lead_days : WINDOW_DEFAULTS.donation_lead_days,
    minute_logging_days: campaign?.minute_logging_days ?? WINDOW_DEFAULTS.minute_logging_days,
    donation_days_after: campaign?.donation_days_after ?? WINDOW_DEFAULTS.donation_days_after,
    // Saving the signup draft's step confirms its dates, even unchanged.
    ...(mode === "confirm" && { dates_confirmed: true }),
    return_to: "setup",
  });
  const { data, setData, errors, processing } = form;
  // Keep the suggested name in step with the season until the admin types their own.
  const [nameTouched, setNameTouched] = useState(mode === "edit");

  const setDates = (start, end) =>
    setData((d) => ({ ...d, start_at: start, end_at: end, name: nameTouched ? d.name : suggestCampaignName(start || d.start_at) }));

  const submit = (e) => {
    e.preventDefault();
    const options = { preserveScroll: true, preserveState: "errors" };
    if (mode === "create") form.post(paths.create_campaign, options);
    else form.patch(paths.update_campaign, options);
  };

  const validDates = data.start_at && data.end_at && data.end_at >= data.start_at;
  const advice = validDates ? dateAdvice({ start: data.start_at, end: data.end_at, today, leadDays: data.donation_lead_days }) : [];
  // A saved any-time campaign opened donations when it was saved that way.
  const savedOpenOn = data.donation_lead_days === null ? campaign?.saved_donations_open_on : null;
  const dates = validDates ? keyDatesFor(data.start_at, data.end_at, data, today, savedOpenOn) : null;
  const baseError = errors.base;

  return (
    <form onSubmit={submit} className="space-y-8">
      {baseError && <StepNotice tone="warning">{baseError}</StepNotice>}

      {/* ── When ─────────────────────────────────────────── */}
      <section className="space-y-4">
        <SectionHeading number="1" title="When is it?" />
        {suggesting && (
          <p className="text-sm text-muted-foreground">
            {initial.fromApplication ? "We've filled in the dates from your signup form." : "We've suggested dates that leave you time to get set up."}{" "}
            Check them against your school calendar for holidays and PD days.
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            name="start_at"
            label="Reading starts"
            type="date"
            required
            value={data.start_at}
            onChange={(e) => setDates(e.target.value, data.end_at)}
            error={errors.start_at}
          />
          <FormField
            name="end_at"
            label="Last day of reading"
            type="date"
            required
            value={data.end_at}
            onChange={(e) => setDates(data.start_at, e.target.value)}
            error={errors.end_at}
          />
        </div>

        {advice.map((item) => (
          <StepNotice
            key={item.text}
            tone={item.tone}
            action={item.fix && (
              <Button type="button" size="sm" variant="outline" onClick={() => setDates(item.fix.start, item.fix.end)}>
                {item.fix.label}
              </Button>
            )}
          >
            {item.text}
          </StepNotice>
        ))}

        <CampaignWindowFields data={data} setData={setData} errors={errors} dates={dates} today={today} />
      </section>

      {/* ── Why ──────────────────────────────────────────── */}
      <section className="space-y-4">
        <SectionHeading number="2" title="What's it for?" />
        <FormField
          name="description"
          label="What you're raising money for"
          error={errors.description}
          help="One sentence families can repeat to a grandparent. It's shown on donation pages, and you can add it to the info sheets."
        >
          <textarea
            id="description"
            name="description"
            rows={3}
            value={data.description}
            onChange={(e) => setData("description", e.target.value)}
            placeholder="We're raising money for new library books while spending two weeks reading together."
            className="flex w-full rounded-lg border border-input bg-card px-4 py-3 text-base text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          />
        </FormField>
        <FormField
          name="name"
          label="Campaign name"
          required
          value={data.name}
          onChange={(e) => {
            setNameTouched(true);
            setData("name", e.target.value);
          }}
          error={errors.name || errors.slug}
          help="Families see it on every page, sheet and receipt. “Spring Read-a-thon 2026” works better than “Q2 Fundraiser.”"
        />
      </section>

      {/* ── Goals ────────────────────────────────────────── */}
      <section className="space-y-4">
        <SectionHeading number="3" title="What's the goal?" optional />
        <p className="text-sm text-muted-foreground">
          Goals show as progress bars on the dashboard and donation pages, and school-wide rewards can be sized to them. Change them any time before the campaign ends.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <MoneyField
            name="money_goal_dollars"
            label="Fundraising goal"
            value={data.money_goal_dollars}
            onChange={(e) => setData("money_goal_dollars", e.target.value)}
            error={errors.money_goal_cents}
          />
          <FormField
            name="minute_goal"
            label="Reading goal (minutes)"
            type="number"
            min="0"
            step="1"
            inputMode="numeric"
            placeholder="50000"
            value={data.minute_goal}
            onChange={(e) => setData("minute_goal", e.target.value)}
            error={errors.minute_goal}
          />
        </div>
        <MinuteGoalHelper
          start={validDates ? data.start_at : null}
          end={validDates ? data.end_at : null}
          knownStudents={counts.students || application?.estimated_students || null}
          fromRoster={counts.students > 0}
          current={data.minute_goal}
          onUse={(goal) => setData("minute_goal", String(goal))}
        />
      </section>

      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-6">
        {suggesting ? (
          <>
            <Button type="submit" size="lg" disabled={processing}>
              {processing ? "Saving…" : mode === "create" ? "Create campaign" : "Save campaign"}
            </Button>
            <p className="text-xs text-muted-foreground">Everything here stays editable until the campaign starts.</p>
          </>
        ) : (
          <>
            <Button type="submit" disabled={processing || !form.isDirty}>{processing ? "Saving…" : "Save changes"}</Button>
            {form.isDirty ? (
              <Button type="button" variant="ghost" onClick={() => form.reset()}>Discard changes</Button>
            ) : (
              <p className="text-xs text-muted-foreground">Saved. Change anything here until the campaign starts.</p>
            )}
          </>
        )}
      </div>
    </form>
  );
}

function SectionHeading({ number, title, optional }) {
  return (
    <h3 className="flex items-center gap-2.5 text-base font-semibold text-foreground">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{number}</span>
      {title}
      {optional && <span className="text-xs font-normal text-muted-foreground">Optional</span>}
    </h3>
  );
}


// Students × 20 minutes a day × campaign days, plus a 30% stretch.
function MinuteGoalHelper({ start, end, knownStudents, fromRoster, current, onUse }) {
  const [typedStudents, setTypedStudents] = useState("");
  const students = knownStudents || Number(typedStudents) || null;
  const suggestion = suggestMinuteGoal({ students, start, end });

  return (
    <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-4 text-sm">
      <p className="flex items-center gap-1.5 font-medium text-foreground">
        <Wand2 className="h-4 w-4 text-primary" />
        Work out a reading goal
      </p>
      <p className="mt-1 text-muted-foreground">
        Most children read 15–20 minutes a day already. Multiply that across the campaign, then add about a third as a stretch.
      </p>

      {!knownStudents && (
        <div className="mt-3 flex items-center gap-2">
          <label htmlFor="estimated_students" className="text-muted-foreground">About how many students?</label>
          <input
            id="estimated_students"
            type="number"
            min="1"
            inputMode="numeric"
            value={typedStudents}
            onChange={(e) => setTypedStudents(e.target.value)}
            className="h-9 w-24 rounded-lg border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      )}

      {suggestion && (
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground font-tabular">
            {suggestion.students.toLocaleString("en-CA")} students{knownStudents && !fromRoster ? " (from your signup form)" : ""} × {MINUTES_PER_DAY} min × {suggestion.days} days
            {" "}≈ {suggestion.baseline.toLocaleString("en-CA")}, plus a stretch:{" "}
            <strong className="text-foreground">{suggestion.goal.toLocaleString("en-CA")} minutes</strong>
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={String(suggestion.goal) === current}
            className={cn(String(suggestion.goal) === current && "opacity-60")}
            onClick={() => onUse(suggestion.goal)}
          >
            {String(suggestion.goal) === current ? "Using it" : `Use ${suggestion.goal.toLocaleString("en-CA")}`}
          </Button>
        </div>
      )}
    </div>
  );
}
